import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useMaarifInitLists from './useMaarifInitLists';
import useLearningActivityAssessmentSummary from './useLearningActivityAssessmentSummary';
import AssessmentTableView from './AssessmentTableView';
import { normalizeSortOrder } from './assessmentUiUtils';

function computeProgress({ students, criteria, itemsByKey }) {
  const st = Array.isArray(students) ? students : [];
  const crit = Array.isArray(criteria) ? criteria : [];
  const criterionIds = crit.filter((c) => c && c.id != null).map((c) => String(c.id));

  const totalExpected = st.length * criterionIds.length;
  if (!totalExpected) {
    return { studentCount: st.length, criterionCount: criterionIds.length, totalExpected: 0, recordedCount: 0, missingCount: 0 };
  }

  let recordedCount = 0;
  for (const s of st) {
    if (!s || s.id == null) continue;
    const sid = String(s.id);
    for (const cid of criterionIds) {
      const k = `${sid}:${cid}`;
      const score = itemsByKey?.[String(k)]?.score;
      const hasScore = score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score));
      if (hasScore) recordedCount += 1;
    }
  }

  return {
    studentCount: st.length,
    criterionCount: criterionIds.length,
    totalExpected,
    recordedCount,
    missingCount: Math.max(0, totalExpected - recordedCount),
  };
}

export default function LearningOutcomeAssessmentSummaryPage({ onError, schoolId }) {
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const notifyError = useCallback((msg) => { onErrorRef.current && onErrorRef.current(msg); }, []);

  const { classes, courses, loadingInit } = useMaarifInitLists({ schoolId, notifyError });

  // Default selections
  useEffect(() => {
    setSelectedClass((prev) => {
      if (!prev?.id) return classes[0] || null;
      const stillThere = (classes || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || (classes[0] || null);
    });
  }, [classes]);

  useEffect(() => {
    setSelectedCourse((prev) => {
      if (!prev?.id) return null;
      const stillThere = (courses || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || null;
    });
  }, [courses]);

  const effectiveLessonId = selectedCourse?.id;

  const { loading, students, tables } = useLearningActivityAssessmentSummary({
    schoolId,
    classId: selectedClass?.id,
    lessonId: effectiveLessonId,
    enabled: !!schoolId && !!selectedClass?.id && !!selectedCourse?.id,
    notifyError,
  });

  const tablesSorted = useMemo(() => {
    const arr = Array.isArray(tables) ? tables : [];
    return arr
      .slice()
      .sort((a, b) => {
        const la = normalizeSortOrder(a?.learning_area_id);
        const lb = normalizeSortOrder(b?.learning_area_id);
        if (la !== lb) return la - lb;
        return String(a?.learning_activity_name || '').localeCompare(String(b?.learning_activity_name || ''), 'tr-TR');
      });
  }, [tables]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Öğrenme Çıktısı Özeti (Salt Okunur)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seçilen şube (ve opsiyonel ders) için tüm öğrenme etkinliklerinin değerlendirme tablolarını görüntüler.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={classes || []}
            loading={loadingInit}
            value={selectedClass}
            onChange={(_, v) => setSelectedClass(v)}
            getOptionLabel={(o) => String(o?.name || '')}
            renderInput={(params) => <TextField {...params} label="Şube" />}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            options={courses || []}
            loading={loadingInit}
            value={selectedCourse}
            onChange={(_, v) => setSelectedCourse(v)}
            getOptionLabel={(o) => String(o?.name || '')}
            renderInput={(params) => <TextField {...params} label="Ders" />}
            renderOption={(props, option) => (
              <li {...props}>
                {String(option?.name || '')}
              </li>
            )}
          />

          {!selectedCourse?.id && (
            <Chip
              color="warning"
              label="Ders seçimi zorunlu"
              sx={{ height: 32 }}
            />
          )}

          <Chip
            label={loading ? 'Yükleniyor…' : `Tablo: ${tablesSorted.length}`}
            variant="outlined"
            sx={{ height: 32 }}
          />
        </Box>
      </Paper>

      {(tablesSorted || []).map((t) => {
        const criteriaSorted = (t?.criteria || [])
          .slice()
          .sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order));

        const itemsByKey = {};
        (t?.items || []).forEach((it) => {
          if (!it || it.student_id == null || it.criterion_id == null) return;
          const k = `${it.student_id}:${it.criterion_id}`;
          itemsByKey[String(k)] = { score: it.score };
        });

        const p = computeProgress({ students, criteria: criteriaSorted, itemsByKey });

        return (
          <Accordion
            key={`${t.assessment_id}`}
            disableGutters
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              '&:before': { display: 'none' },
              '@media print': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
                '& .MuiCollapse-root': {
                  height: 'auto !important',
                  overflow: 'visible !important',
                  visibility: 'visible !important',
                },
                '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                  display: 'block',
                },
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t.learning_activity_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.learning_area_name} • {t.assessment_name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Chip size="small" label={`Kayıt: ${p.recordedCount}/${p.totalExpected}`} />
                  <Chip size="small" color={p.missingCount === 0 ? 'success' : 'default'} label={`Eksik: ${p.missingCount}`} />
                  {t.status ? <Chip size="small" variant="outlined" label={String(t.status)} /> : <Chip size="small" variant="outlined" label="Oturum yok" />}
                  {t.assessed_on ? <Chip size="small" variant="outlined" label={String(t.assessed_on)} /> : null}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <AssessmentTableView
                students={students}
                sortedCriteria={criteriaSorted}
                focusedCriterionId={null}
                itemsByKey={itemsByKey}
                onSetScore={null}
                onFillUnsetScores={null}
                readOnly
                printable
                savingBulk={false}
                isSavingScore={() => false}
                bands={t?.bands || []}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}

      {!loading && tablesSorted.length === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Seçilen filtreler için gösterilecek değerlendirme tablosu bulunamadı.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
